/**
 * DP92 Part D — ONE MASTHEAD IMAGE PER PAGE, and one place that decides which.
 *
 * DP90 found what was missing: fields keyed by page, a `page` prop on
 * MastheadBand, an image path for the Work Table's `.kmw-mast` which had none,
 * and a page selector in the Branding UI. This is the resolver the first three
 * share.
 *
 * A page with no entry falls back to MY DESK's. My Desk with no entry falls back
 * to the single `masthead_image` every tenant already has, so nothing saved
 * before this pass loses its masthead.
 *
 * DP97 Part 1 — AND THE WHOLE SETTING PER PAGE, not just the image. DP92 keyed
 * the image and left the KIND ('planes' | 'flat' | 'image'), the crops and the
 * scrim at tenant level, so uploading a picture for one gateway switched all
 * three to 'image' and handed all three the same picture. `masthead_pages`
 * carries kind, image and colour per page; the crops and the scrim are still
 * tenant-wide and are reported as such.
 */
export const MASTHEAD_PAGES = [
  { id: 'mydesk', label: 'My Desk' },
  { id: 'projects', label: 'All Projects' },
  { id: 'worktable', label: 'Work Table' },
];

/**
 * DP100 Part 4 — THE NINE POSITIONS, as a three-by-three grid. No free
 * dragging, as ruled. `top-left` is where every band sits today, so it is the
 * default and the only one that keeps the full-width layout: a block that fills
 * the width has nowhere sideways to go.
 */
export const MASTHEAD_ANCHOR_ROWS = [
  [{ id: 'top-left', label: 'Top left' }, { id: 'top-centre', label: 'Top centre' }, { id: 'top-right', label: 'Top right' }],
  [{ id: 'middle-left', label: 'Middle left' }, { id: 'middle-centre', label: 'Middle centre' }, { id: 'middle-right', label: 'Middle right' }],
  [{ id: 'bottom-left', label: 'Bottom left' }, { id: 'bottom-centre', label: 'Bottom centre' }, { id: 'bottom-right', label: 'Bottom right' }],
];
const ANCHORS = MASTHEAD_ANCHOR_ROWS.flat().map((a) => a.id);
export const DEFAULT_ANCHOR = 'top-left';
export const DEFAULT_PLATE_STRENGTH = 72;

const KINDS = ['planes', 'flat', 'image'];

const usable = (entry) => (entry && typeof entry === 'object' && entry.url) ? entry : null;
const num = (v) => (typeof v === 'number' && Number.isFinite(v) ? Math.min(100, Math.max(0, v)) : null);

/**
 * The tenant's own scrim, read as a strength, so a tenant who never touches the
 * new per-page control keeps exactly the plate it has: off 0, standard 0.72,
 * strong 0.88 — DP95's three steps, expressed on the range that replaced them.
 */
const tenantStrength = (s) => {
  if (s.masthead_image_scrim === false) return 0;
  if (s.masthead_image_scrim_strength === 'strong') return 88;
  if (s.masthead_image_scrim_strength === 'off') return 0;
  return DEFAULT_PLATE_STRENGTH;
};

/**
 * DP103 ruling 4 — WHETHER THIS PAGE HAS AN ENTRY OF ITS OWN.
 *
 * Every field used to fall back on its own (`own.type_colour ||
 * desk.type_colour`), so setting only My Desk's type colour repainted All
 * Projects and the Work Table even though both held their own entries — and
 * their admin fields then showed My Desk's value as though it were theirs. A
 * page with an entry now uses its own values and the tenant's, never My Desk's.
 */
const hasOwnEntry = (own) => !!(own && (own.kind || own.image?.url || own.colour
  || own.plate_colour || typeof own.plate_strength === 'number' || own.type_colour
  || own.anchor || own.crop_wide || own.crop_narrow));

export const resolveMastheadImage = (settings, page = 'mydesk') => {
  const byPage = (settings && settings.masthead_pages) || {};
  const legacy = (settings && settings.masthead_images) || {};
  // DP103 ruling 4 — a page that has its OWN entry does not take My Desk's
  // picture. Only a page with no entry at all inherits.
  const inherits = page !== 'mydesk' && !hasOwnEntry(byPage[page]);
  return usable(byPage[page]?.image) || usable(legacy[page])
    || (inherits ? (usable(byPage.mydesk?.image) || usable(legacy.mydesk)) : null)
    || usable(settings?.masthead_image) || null;
};

/**
 * The whole masthead for one page, with every fallback applied in order:
 * the page's own entry, then My Desk's entry, then the tenant-level fields DP19
 * shipped. `inherited` says whether this page is drawing something of its own.
 */
export const resolveMasthead = (settings, page = 'mydesk') => {
  const s = settings || {};
  const byPage = s.masthead_pages || {};
  const own = byPage[page] || {};
  // DP103 ruling 4 — My Desk's entry is read ONLY by a page that has none.
  const desk = (page !== 'mydesk' && !hasOwnEntry(own)) ? (byPage.mydesk || {}) : {};
  const image = resolveMastheadImage(s, page);
  const kind = KINDS.includes(own.kind) ? own.kind
    : KINDS.includes(desk.kind) ? desk.kind
      : KINDS.includes(s.masthead_background) ? s.masthead_background : 'planes';
  const colour = own.colour || desk.colour || null;
  // DP100 Part 4 — the plate, the type colour and the position are per page,
  // with the same fallback chain: this page, then My Desk, then the default.
  const plateColour = own.plate_colour || desk.plate_colour || null;
  const plateStrength = num(own.plate_strength) ?? num(desk.plate_strength) ?? tenantStrength(s);
  const typeColour = own.type_colour || desk.type_colour || null;
  const ownAnchor = ANCHORS.includes(own.anchor) ? own.anchor : null;
  const deskAnchor = ANCHORS.includes(desk.anchor) ? desk.anchor : null;
  return {
    kind,
    image,
    colour,
    plateColour,
    plateStrength,
    typeColour,
    anchor: ownAnchor || deskAnchor || DEFAULT_ANCHOR,
    // the crops are the tenant's pair unless this page's own editor derived its
    // own (DP101); a page with no entry takes My Desk's, like everything else
    cropWide: own.crop_wide || desk.crop_wide || s.masthead_image_crop_wide || null,
    cropNarrow: own.crop_narrow || desk.crop_narrow || s.masthead_image_crop_narrow || null,
    hasOwn: hasOwnEntry(own),
    // a page set to 'image' with no image anywhere shows no picture: the band
    // falls back to its plain colour, which reads as 'flat'
    imageMissing: kind === 'image' && !image,
  };
};

export default resolveMastheadImage;
